import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useCityGroomerStaffListAPI } from "@/app/hooks/api_hooks/configuration/useCityGroomerStaffListAPI";
import { useClinicGroomerMappingAddAPI } from "@/app/hooks/api_hooks/configuration/useClinicGroomerMappingAddAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

type GroomerOption = {
  id: string;
  title: string;
  subtitle?: string;
  meta?: any;
};

type ClinicItem = {
  city_id: string;
  groomer_ids: any;
  clinic_name?: string;
  city?: string;
  id?: string;
};

interface GroomerModalProps {
  open: boolean;
  onClose: () => void;
  clinic: ClinicItem | null;
  onSubmitSuccess: () => void;
}

const GroomerModalComponent = ({ open, onClose, clinic, onSubmitSuccess }: GroomerModalProps) => {
  const { execute: mapClinicGroomer } = useClinicGroomerMappingAddAPI();
  const { execute: fetchCityGroomerList, groomerOptions } = useCityGroomerStaffListAPI();
  const [selectedGroomers, setSelectedGroomers] = useState([]);

  const formHook = useForm<any>({
    mode: "onChange",
  });

  const onSubmit = async () => {
    const groomerIds = formHook.watch("groomer_id") || [];
    await mapClinicGroomer(clinic?.id, { groomer_ids: groomerIds });
    onClose();
    onSubmitSuccess();
  };

  useEffect(() => {
    if (clinic?.city_id) {
      fetchCityGroomerList(clinic?.city_id);
    }
  }, [clinic]);

  useEffect(() => {
    if (clinic?.groomer_ids && groomerOptions?.length > 0) {
      formHook.setValue("groomer_id", clinic?.groomer_ids);
    }
  }, [clinic, groomerOptions]);

  const groomerOptionsItem = useSelectMenuReducer(groomerOptions, "display_name", "groomer_id");

  useEffect(() => {
    if (formHook.watch("groomer_id")) {
      const filter = groomerOptionsItem.filter((id) => formHook.watch("groomer_id").includes(id.id));
      setSelectedGroomers(filter);
    }
  }, [formHook.watch("groomer_id")]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-visible rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                <div className="flex items-start justify-between">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-slate-900">{clinic?.clinic_name}</Dialog.Title>
                    <p className="text-sm text-slate-500">{clinic?.city}</p>
                  </div>
                  <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
                    ✕
                  </button>
                </div>

                <div className="mt-4 rounded-xl bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">Mapped Groomers</p>
                  {selectedGroomers?.length === 0 ? (
                    <p className="mt-2 text-sm text-slate-500">No groomers selected.</p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedGroomers?.map((groomer) => (
                        <span
                          key={groomer.id}
                          className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-sm font-medium text-slate-800 ring-1 ring-slate-200"
                        >
                          {groomer.title}
                          <button
                            type="button"
                            className="text-rose-500 hover:text-rose-600"
                            onClick={() => {
                              const updated = selectedGroomers.filter((item) => item.id !== groomer.id);
                              setSelectedGroomers(updated);
                              const updatedGroomer = formHook.watch("groomer_id").filter((i) => i !== groomer.id);
                              formHook.setValue("groomer_id", updatedGroomer);
                            }}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <SearchableMultiSelectMenu
                    onSelectItem={(items) => {
                      const ids = items.map((i) => i.id);
                      formHook.setValue("groomer_id", ids);
                    }}
                    withSelectAll
                    showTooltips
                    withDropDownSearch
                    errorMessages={[{ message: "Required", type: "required" }]}
                    fieldError={formHook.formState.errors.groomer_id}
                    register={formHook.register("groomer_id", {
                      required: true,
                    })}
                    showTypedErrors={false}
                    containerClassName="ml-2"
                    placeholder="Select Groomer"
                    defaultSelected={groomerOptionsItem?.filter((c) => formHook.watch("groomer_id")?.includes(c.id))}
                    selectItems={groomerOptionsItem}
                  />
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onSubmit}
                    disabled={formHook.watch("groomer_id")?.length === 0}
                    className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    Add Groomers
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default GroomerModalComponent;
